import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { adminMenu, getTestModelMenu, Menu, MenuOption, SubMenuOption, userMenu } from './menu.utils';
import { OptionComponent } from '@ng-cds/ui-components/option';
import { NgClass } from '@angular/common';
import { MenuComponent } from '@ng-cds/ui-components/menu';
import { FeatureFlagService, UserService } from '@vallia-doc-flow/ui-auth';

@Component({
  selector: 'vallia-doc-flow-left-menu',
  standalone: true,
  imports: [NgClass, OptionComponent, MenuComponent],
  templateUrl: './left-menu.component.html',
  styleUrl: './left-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeftMenuComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private changeDetector = inject(ChangeDetectorRef);
  private featureService = inject(FeatureFlagService);
  private userService = inject(UserService);

  subscription = new Subscription();
  expandedItems = new Set<string>();
  activeItem!: MenuOption | SubMenuOption;

  get menu(): Menu[] {
    const testMenu = getTestModelMenu(this.featureService);
    if (testMenu.length > 0) return testMenu;
    return this.isAdmin ? adminMenu : userMenu(this.featureService, this.userService);
  }

  get isAdmin(): boolean {
    const userDetails = sessionStorage.getItem('userDetails');
    if (userDetails) {
      const roleType = JSON.parse(userDetails).role_type;
      return roleType === 'admin' || roleType === 'suAdmin';
    }
    return false;
  }

  ngOnInit(): void {
    this.restoreMenuState();

    const subscription$ = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.setCurrentMenuItem(event.url);
        this.changeDetector.markForCheck();
      });

    this.subscription.add(subscription$);
    this.setCurrentMenuItem(this.router.url);
  }

  handleMenuItemClick(menuOption: MenuOption): void {
    if (menuOption.subMenu?.length) {
      this.toggleSubMenu(menuOption.id);
    } else if (!menuOption.isDisabled) {
      this.navigateToPage(menuOption);
    }
  }

  navigateToPage(menuOption: MenuOption | SubMenuOption): void {
    this.router.navigate([menuOption.routerLink]).then(success => {
      if (success) {
        this.activeItem = menuOption;
        this.saveMenuState();
        this.changeDetector.markForCheck();
      }
    });
  }

  setCurrentMenuItem(routeLink: string): void {
    let found = false;

    for (const section of this.menu) {
      for (const option of section.sectionOptions) {
        if (option.subMenu?.length) {
          for (const subItem of option.subMenu) {
            if (routeLink.includes(subItem.routerLink)) {
              this.activeItem = subItem;
              this.expandedItems.add(option.id);
              this.saveMenuState();
              found = true;
              break;
            }
          }
        }

        if (!found && option.parentRoutePath && routeLink.includes(option.parentRoutePath)) {
          this.activeItem = option;
          this.saveMenuState();
        }

        if (found) break;
      }
      if (found) break;
    }

    this.changeDetector.markForCheck();
  }

  toggleSubMenu(itemId: string): void {
    if (this.expandedItems.has(itemId)) {
      this.expandedItems.delete(itemId);
    } else {
      this.expandedItems.add(itemId);
    }
    this.saveMenuState();
    this.changeDetector.markForCheck();
  }

  setActiveMenuItem(subMenuOption: SubMenuOption): void {
    this.menu.forEach(section => {
      section.sectionOptions.forEach(option => {
        if (option.subMenu?.some(sub => sub.routerLink === subMenuOption.routerLink)) {
          this.activeItem = option;
          this.expandedItems.add(option.id);
          this.saveMenuState();
          this.changeDetector.markForCheck();
        }
      });
    });
  }

  isSubMenuSelected(menuItem: MenuOption): boolean {
    return menuItem.subMenu?.some(subItem => this.activeItem?.routerLink === subItem.routerLink) ?? false;
  }

  saveMenuState(): void {
    const menuState = {
      activeItem: this.activeItem?.routerLink ?? null,
      expandedItems: Array.from(this.expandedItems),
    };
    localStorage.setItem('leftMenuState', JSON.stringify(menuState));
  }

  restoreMenuState(): void {
    const savedState = localStorage.getItem('leftMenuState');
    if (savedState) {
      const { activeItem, expandedItems } = JSON.parse(savedState);
      if (activeItem) {
        const foundItem = this.findMenuItem(activeItem);
        if (foundItem) {
          this.activeItem = foundItem;
        }
      }
      if (expandedItems) {
        this.expandedItems = new Set(expandedItems);
      }
      this.changeDetector.markForCheck();
    }
  }

  findMenuItem(routerLink: string): MenuOption | SubMenuOption | undefined {
    for (const section of this.menu) {
      for (const option of section.sectionOptions) {
        if (option.routerLink === routerLink) {
          return option;
        }
        if (option.subMenu?.length) {
          const foundSubItem = option.subMenu.find(sub => sub.routerLink === routerLink);
          if (foundSubItem) return foundSubItem;
        }
      }
    }
    return undefined;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
